import { useCallback, useEffect, useRef, useState } from "react";
import type { Identifier } from "ra-core";
import { useGetIdentity, useNotify } from "ra-core";
import {
  Phone,
  PhoneIncoming,
  PhoneMissed,
  PhoneOutgoing,
  Play,
  Send,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { getSupabaseClient } from "../providers/supabase/supabase";

const BRIDGE_URL =
  import.meta.env.VITE_BRIDGE_URL || "https://crm.k-panel.ru/bridge";

const CHANNEL_LABELS: Record<string, string> = {
  whatsapp: "WhatsApp",
  telegram: "Telegram",
  max: "Max",
  avito: "Авито",
  phone: "Звонок",
};

const SENDABLE_CHANNELS = ["whatsapp", "telegram", "max", "avito"];

export interface CommunicationRow {
  id: number;
  created_at: string;
  type: string;
  channel: string;
  direction: string | null;
  contact_id: number | null;
  master_id: number | null;
  deal_id: number | null;
  sales_id: number | null;
  body: string | null;
  attachments: { src: string; type: string }[];
  status: string | null;
  meta: Record<string, unknown>;
}

interface FeedProps {
  contactIds?: Identifier[];
  masterId?: Identifier;
  dealId?: Identifier;
  /** phone to allow starting a brand-new WhatsApp chat */
  phone?: string | null;
}

/** Unified communication feed: messages from all channels + calls. */
export const CommunicationFeed = ({
  contactIds,
  masterId,
  dealId,
  phone,
}: FeedProps) => {
  const [rows, setRows] = useState<CommunicationRow[]>([]);
  const [text, setText] = useState("");
  const [channel, setChannel] = useState<string>("whatsapp");
  const [sending, setSending] = useState(false);
  const notify = useNotify();
  const { identity } = useGetIdentity();
  const bottomRef = useRef<HTMLDivElement | null>(null);

  const load = useCallback(async () => {
    const supabase = getSupabaseClient();
    let query = supabase
      .from("communications")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(100);

    const parts: string[] = [];
    if (contactIds?.length)
      parts.push(`contact_id.in.(${contactIds.join(",")})`);
    if (masterId) parts.push(`master_id.eq.${masterId}`);
    if (!parts.length && dealId) parts.push(`deal_id.eq.${dealId}`);
    if (!parts.length) {
      setRows([]);
      return;
    }
    query = query.or(parts.join(","));
    const { data, error } = await query;
    if (!error && data) setRows(data.reverse() as CommunicationRow[]);
  }, [JSON.stringify(contactIds), masterId, dealId]);

  useEffect(() => {
    load();
  }, [load]);

  // Live updates for the feed
  useEffect(() => {
    const supabase = getSupabaseClient();
    const subscription = supabase
      .channel(
        `feed-${masterId ?? ""}-${dealId ?? ""}-${contactIds?.join("-") ?? ""}`,
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "communications" },
        () => load(),
      )
      .subscribe((subscriptionStatus, err) => {
        if (subscriptionStatus !== "SUBSCRIBED") {
          console.warn("feed realtime:", subscriptionStatus, err?.message);
        }
      });
    return () => {
      supabase.removeChannel(subscription);
    };
  }, [load]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: "end" });
  }, [rows.length]);

  const send = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      const { data: sessionData } = await getSupabaseClient().auth.getSession();
      const token = sessionData.session?.access_token;
      const response = await fetch(`${BRIDGE_URL}/send`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          channel,
          text: text.trim(),
          contact_id: contactIds?.[0] ?? null,
          master_id: masterId ?? null,
          deal_id: dealId ?? null,
          phone: phone ?? null,
        }),
      });
      if (response.status === 503) {
        notify(
          "Мессенджеры ещё не подключены (Wazzup). Сообщение не отправлено.",
          {
            type: "warning",
          },
        );
      } else if (response.status === 409) {
        notify("В этом канале ещё нет переписки с клиентом.", {
          type: "warning",
        });
      } else if (!response.ok) {
        notify("Не удалось отправить сообщение.", { type: "error" });
      } else {
        setText("");
      }
    } catch {
      notify("Не удалось отправить сообщение.", { type: "error" });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex-1 overflow-y-auto pr-1 flex flex-col gap-2 min-h-40 max-h-[55vh]">
        {rows.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-8">
            Пока нет сообщений и звонков
          </p>
        )}
        {rows.map((row) => (
          <FeedItem key={row.id} row={row} myName={identity?.fullName} />
        ))}
        <div ref={bottomRef} />
      </div>

      <div className="mt-3 flex flex-col gap-2 border-t pt-3">
        <div className="flex gap-2">
          <Select value={channel} onValueChange={setChannel}>
            <SelectTrigger className="w-32 h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SENDABLE_CHANNELS.map((ch) => (
                <SelectItem key={ch} value={ch}>
                  {CHANNEL_LABELS[ch] ?? ch}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Textarea
            value={text}
            onChange={(event) => setText(event.target.value)}
            placeholder="Сообщение клиенту..."
            rows={1}
            className="flex-1 min-h-9 resize-none"
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                send();
              }
            }}
          />
          <Button
            onClick={send}
            disabled={sending || !text.trim()}
            className="h-9"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

const FeedItem = ({
  row,
  myName,
}: {
  row: CommunicationRow;
  myName?: string;
}) => {
  if (row.type === "call") return <CallItem row={row} />;
  return <MessageItem row={row} myName={myName} />;
};

const timeLabel = (iso: string) =>
  new Date(iso).toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });

const MessageItem = ({
  row,
  myName,
}: {
  row: CommunicationRow;
  myName?: string;
}) => {
  const isOut = row.direction === "out";
  const author = isOut
    ? myName || "Менеджер"
    : (row.meta?.authorName as string) || "Клиент";
  return (
    <div className={`flex ${isOut ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${
          isOut ? "bg-primary/10" : "bg-muted"
        }`}
      >
        <div className="flex items-center gap-2 text-[11px] text-muted-foreground mb-0.5">
          <span>{CHANNEL_LABELS[row.channel] ?? row.channel}</span>
          <span>{author}</span>
          <span>{timeLabel(row.created_at)}</span>
          {isOut && row.status && row.status !== "sent" ? (
            <span>· {row.status}</span>
          ) : null}
        </div>
        <p className="whitespace-pre-line break-words">{row.body}</p>
        {row.attachments?.map((attachment, index) => (
          <a
            key={index}
            href={attachment.src}
            target="_blank"
            rel="noreferrer"
            className="text-xs underline"
          >
            Вложение
          </a>
        ))}
      </div>
    </div>
  );
};

const CallItem = ({ row }: { row: CommunicationRow }) => {
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const missed = row.status === "missed" || row.status === "busy";
  const duration = (row.meta?.duration as number) || 0;
  const recording = row.meta?.recording as string | null;
  const number = (row.meta?.number as string) || "";

  const Icon = missed
    ? PhoneMissed
    : row.direction === "in"
      ? PhoneIncoming
      : PhoneOutgoing;

  const loadRecording = async () => {
    if (!recording || audioUrl) return;
    const { data } = await getSupabaseClient()
      .storage.from("recordings")
      .createSignedUrl(recording, 3600);
    if (data?.signedUrl) setAudioUrl(data.signedUrl);
  };

  return (
    <div className="flex justify-center">
      <div
        className={`w-full rounded-lg border px-3 py-2 text-sm flex flex-col gap-1 ${
          missed ? "border-red-500/40 bg-red-500/5" : "bg-card"
        }`}
      >
        <div className="flex items-center gap-2">
          <Icon
            className={`w-4 h-4 ${missed ? "text-red-500" : "text-green-600"}`}
          />
          <span className="font-medium">
            {missed
              ? "Пропущенный звонок"
              : row.direction === "in"
                ? "Входящий звонок"
                : "Исходящий звонок"}
          </span>
          <span className="text-xs text-muted-foreground">{number}</span>
          <span className="text-xs text-muted-foreground ml-auto">
            {timeLabel(row.created_at)}
          </span>
        </div>
        {!missed && (
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <span>
              {Math.floor(duration / 60)}:
              {String(duration % 60).padStart(2, "0")}
            </span>
            {recording && !audioUrl && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2"
                onClick={loadRecording}
              >
                <Play className="w-3 h-3 mr-1" /> Запись
              </Button>
            )}
            {audioUrl && (
              <audio controls src={audioUrl} className="h-8 max-w-56" />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export const CommunicationFeedTitle = () => (
  <div className="flex items-center gap-2 mb-2">
    <Phone className="w-4 h-4 text-muted-foreground" />
    <h3 className="text-base font-medium">Коммуникации</h3>
  </div>
);
